'use client';

import { useMemo } from 'react';

import { DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER, MAX_PAGE_SIZE } from '@/constants/variables';
import type { QuizListItem, QuizListServiceConfig } from '@/services/httpServices/QuizServices/types';
import type { SequenceListItem, SequenceListServiceConfig } from '@/services/httpServices/SequenceServices/types';
import type { FormOption } from '@/types/general';
import { getCurrentLocaleClient } from '@/utils/language-helpers';

import { useQueryConfig } from './useQueryConfig';
import { useQuizQuery } from './useQuizQuery';
import { useSequenceQuery } from './useSequenceQuery';

type AvailableUnitsReturn = {
  sequences: {
    isLoading: boolean;
    isError: boolean;
    error?: Error | null;
    options: FormOption[];
    data: SequenceListItem[];
    refetch: () => Promise<unknown>;
  };
  quizzes: {
    isLoading: boolean;
    isError: boolean;
    error?: Error | null;
    options: FormOption[];
    data: QuizListItem[];
    refetch: () => Promise<unknown>;
  };
  isLoadingAny: boolean;
  hasErrors: boolean;
};

/**
 * Hook to fetch available sequences and quizzes for course content management
 * @param isDisabled - Whether to disable the queries
 * @param language - Optional language override for fetching content (defaults to user's locale)
 * @returns Object containing sequences and quizzes data with loading and error states
 */
export function useAvailableUnits(isDisabled: boolean, language?: string): AvailableUnitsReturn {
  const { isEnabled, httpRequestConfig } = useQueryConfig();
  const userLocale = useMemo(() => getCurrentLocaleClient(), []);
  const effectiveLanguage = language !== undefined && language !== '' ? language : userLocale;

  const queryParams = useMemo(() => {
    const baseParams = {
      httpRequestConfig,
      params: {
        query: {
          paginationFirst: MAX_PAGE_SIZE,
          paginationAfter: '',
          paginationLast: undefined,
          paginationBefore: undefined,
          sortByField: DEFAULT_SORT_FIELD,
          sortByOrder: DEFAULT_SORT_ORDER,
          searchTerm: '',
          language: effectiveLanguage,
          searchByType: 'title' as const,
          filters: '',
        },
      },
    };

    return {
      sequences: baseParams as SequenceListServiceConfig,
      quizzes: baseParams as QuizListServiceConfig,
    };
  }, [httpRequestConfig, effectiveLanguage]);

  const isQueryEnabled = isEnabled && isDisabled === false && queryParams.sequences !== undefined;

  const sequenceQuery = useSequenceQuery(
    queryParams.sequences!,
    isQueryEnabled,
  );

  const quizQuery = useQuizQuery(
    queryParams.quizzes!,
    isQueryEnabled,
  );

  return useMemo(() => {
    const sequenceOptions: FormOption[] = sequenceQuery.data?.nodes?.map(node => ({
      value: node.id,
      label: node.title,
    })) || [];

    const quizOptions: FormOption[] = quizQuery.data?.nodes?.map(node => ({
      value: node.id,
      label: node.title,
    })) || [];

    return {
      sequences: {
        isLoading: sequenceQuery.isLoading,
        isError: sequenceQuery.isError,
        error: sequenceQuery.error,
        options: sequenceOptions,
        data: sequenceQuery.data?.nodes || [],
        refetch: sequenceQuery.refetch,
      },
      quizzes: {
        isLoading: quizQuery.isLoading,
        isError: quizQuery.isError,
        error: quizQuery.error,
        options: quizOptions,
        data: quizQuery.data?.nodes || [],
        refetch: quizQuery.refetch,
      },
      isLoadingAny: sequenceQuery.isLoading || quizQuery.isLoading,
      hasErrors: sequenceQuery.isError || quizQuery.isError,
    };
  }, [sequenceQuery, quizQuery]);
}
